export const CREATOR_WORKFLOW_STATUSES = [
  "Added",
  "Contacted",
  "Negotiating",
  "Accepted",
  "Posted",
  "Paid",
  "Completed",
];

export const CREATOR_OFFER_TYPES = [
  "Paid",
  "Gifted",
  "Affiliate",
  "Hybrid",
  "Other",
];

export const CREATOR_CONTENT_TYPES = [
  "Instagram Post",
  "Instagram Reel",
  "Instagram Story",
  "TikTok Video",
  "YouTube Short",
  "YouTube Video",
  "Other",
];

export const CREATOR_WORKFLOW_DEFAULTS = {
  workflowStatus: "Added",
  offerType: "",
  offerValue: "",
  contentType: "",
  contentUrl: "",
  notes: "",
  contactedAt: "",
  postedAt: "",
  paidAt: "",
};

export const WORKFLOW_ROW_IDLE = "idle";
export const WORKFLOW_ROW_SAVING = "saving";
export const WORKFLOW_ROW_ERROR = "error";
export const WORKFLOW_ROW_SAVED = "saved";

const WORKFLOW_STATUS_INDEX = new Map(
  CREATOR_WORKFLOW_STATUSES.map((status, index) => [status, index])
);

const NORMALIZED_STATUS_MAP = new Map(
  CREATOR_WORKFLOW_STATUSES.map((status) => [status.toLowerCase(), status])
);

const NORMALIZED_OFFER_TYPE_MAP = new Map(
  CREATOR_OFFER_TYPES.map((value) => [value.toLowerCase(), value])
);

const NORMALIZED_CONTENT_TYPE_MAP = new Map(
  CREATOR_CONTENT_TYPES.map((value) => [value.toLowerCase(), value])
);

export function safeStr(value) {
  return String(value ?? "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
}

export function norm(value) {
  return safeStr(value);
}

export function stripAt(value) {
  return norm(value).replace(/^@+/, "");
}

export function creatorKeyOf(value) {
  return stripAt(value).toLowerCase();
}

function normalizeFromMap(value, map, fallback = "") {
  const raw = norm(value);
  if (!raw) return fallback;
  return map.get(raw.toLowerCase()) || fallback;
}

export function normalizeEnum(value, allowedValues, fallback = "") {
  const raw = norm(value);
  if (!raw) return fallback;

  const direct = allowedValues.find((x) => x === raw);
  if (direct) return direct;

  const lower = raw.toLowerCase();
  const ci = allowedValues.find((x) => String(x).toLowerCase() === lower);
  return ci || fallback;
}

export function isValidHttpUrl(value) {
  const s = norm(value);
  if (!s) return true;

  try {
    const url = new URL(s);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function isValidDateString(value) {
  const s = norm(value);
  if (!s) return true;

  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
  const isoDateTime =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?$/;

  if (!isoDateOnly.test(s) && !isoDateTime.test(s)) {
    return false;
  }

  const t = new Date(s).getTime();
  return Number.isFinite(t);
}

export function normalizeNullableText(value) {
  return norm(value);
}

export function getWorkflowStatusIndex(status) {
  const normalized = normalizeFromMap(
    status,
    NORMALIZED_STATUS_MAP,
    CREATOR_WORKFLOW_DEFAULTS.workflowStatus
  );
  return WORKFLOW_STATUS_INDEX.get(normalized) ?? 0;
}

export function isWorkflowAtOrBeyond(status, targetStatus) {
  return getWorkflowStatusIndex(status) >= getWorkflowStatusIndex(targetStatus);
}

export function isCreatorInvited(status) {
  return isWorkflowAtOrBeyond(status, "Contacted");
}

export function isCreatorInviteActive(status) {
  return isWorkflowAtOrBeyond(status, "Accepted");
}

export function isCreatorLinkShared(record = {}) {
  if (norm(record?.contentUrl)) return true;
  if (isWorkflowAtOrBeyond(record?.workflowStatus, "Posted")) return true;
  return false;
}

export function normalizeWorkflowRecord(raw = {}) {
  const username = stripAt(
    raw.username ||
      raw.handle ||
      raw.igHandle ||
      raw.instagram ||
      raw.creatorHandle ||
      ""
  );

  const displayName = norm(
    raw.displayName ||
      raw.full_name ||
      raw.name ||
      username ||
      raw.creatorName ||
      ""
  );

  const creatorKey = creatorKeyOf(
    raw.creatorKey || username || displayName || raw.id || raw._id || ""
  );

  return {
    campaignId: norm(raw.campaignId || raw.campaign_id),
    creatorKey,
    username,
    displayName,
    workflowStatus: normalizeFromMap(
      raw.workflowStatus,
      NORMALIZED_STATUS_MAP,
      CREATOR_WORKFLOW_DEFAULTS.workflowStatus
    ),
    offerType: normalizeFromMap(raw.offerType, NORMALIZED_OFFER_TYPE_MAP, ""),
    offerValue: normalizeNullableText(raw.offerValue),
    contentType: normalizeFromMap(
      raw.contentType,
      NORMALIZED_CONTENT_TYPE_MAP,
      ""
    ),
    contentUrl: normalizeNullableText(raw.contentUrl),
    notes: normalizeNullableText(raw.notes),
    contactedAt: normalizeNullableText(raw.contactedAt),
    postedAt: normalizeNullableText(raw.postedAt),
    paidAt: normalizeNullableText(raw.paidAt),
    createdAt: normalizeNullableText(raw.createdAt),
    updatedAt: normalizeNullableText(raw.updatedAt),
    creatorRef: raw.creatorRef ?? null,
  };
}

export function validateWorkflowDraft(draft = {}) {
  const errors = {};

  if (!creatorKeyOf(draft.creatorKey || draft.username || draft.displayName)) {
    errors.creatorKey = "Missing creator key.";
  }

  if (
    norm(draft.workflowStatus) &&
    !normalizeFromMap(draft.workflowStatus, NORMALIZED_STATUS_MAP, "")
  ) {
    errors.workflowStatus = "Invalid workflow status.";
  }

  if (
    norm(draft.offerType) &&
    !normalizeFromMap(draft.offerType, NORMALIZED_OFFER_TYPE_MAP, "")
  ) {
    errors.offerType = "Invalid offer type.";
  }

  if (
    norm(draft.contentType) &&
    !normalizeFromMap(draft.contentType, NORMALIZED_CONTENT_TYPE_MAP, "")
  ) {
    errors.contentType = "Invalid content type.";
  }

  if (!isValidHttpUrl(draft.contentUrl)) {
    errors.contentUrl = "Enter a valid http/https URL.";
  }

  if (!isValidDateString(draft.contactedAt)) {
    errors.contactedAt = "Invalid contacted date.";
  }

  if (!isValidDateString(draft.postedAt)) {
    errors.postedAt = "Invalid posted date.";
  }

  if (!isValidDateString(draft.paidAt)) {
    errors.paidAt = "Invalid paid date.";
  }

  return errors;
}

export function buildWorkflowSummary(items = []) {
  const list = Array.isArray(items) ? items : [];

  return {
    totalCreators: list.length,
    added: list.filter((x) => x?.workflowStatus === "Added").length,
    contacted: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Contacted")
    ).length,
    negotiating: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Negotiating")
    ).length,
    accepted: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Accepted")
    ).length,
    posted: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Posted")
    ).length,
    paid: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Paid")
    ).length,
    completed: list.filter((x) =>
      isWorkflowAtOrBeyond(x?.workflowStatus, "Completed")
    ).length,
  };
}

/**
 * This is the important addition.
 * It translates workflow rows into invite-style metrics
 * so Campaign Details can stay in sync.
 */
export function buildInviteSummaryFromWorkflows(items = []) {
  const list = Array.isArray(items) ? items : [];

  return {
    total: list.filter((x) => isCreatorInvited(x?.workflowStatus)).length,
    active: list.filter((x) => isCreatorInviteActive(x?.workflowStatus)).length,
    shared: list.filter((x) => isCreatorLinkShared(x)).length,
  };
}

export function mergeCreatorsWithWorkflows(creators = [], workflows = []) {
  const creatorList = Array.isArray(creators) ? creators : [];
  const workflowList = Array.isArray(workflows) ? workflows : [];

  const workflowMap = new Map();

  workflowList.forEach((item) => {
    const normalized = normalizeWorkflowRecord(item);
    const key = creatorKeyOf(
      normalized.creatorKey || normalized.username || normalized.displayName
    );
    if (!key) return;
    workflowMap.set(key, normalized);
  });

  const merged = creatorList.map((creator) => {
    const username = stripAt(
      creator?.username ||
        creator?.handle ||
        creator?.igHandle ||
        creator?.instagram ||
        ""
    );

    const displayName = norm(
      creator?.full_name ||
        creator?.name ||
        creator?.displayName ||
        username ||
        "Creator"
    );

    const creatorKey = creatorKeyOf(username || displayName);
    const workflow = workflowMap.get(creatorKey);

    return normalizeWorkflowRecord({
      ...CREATOR_WORKFLOW_DEFAULTS,
      creatorKey,
      username,
      displayName,
      ...(workflow || {}),
      campaignId: norm(
        workflow?.campaignId || creator?.campaignId || creator?.campaign_id || ""
      ),
      creatorRef: creator,
    });
  });

  const mergedKeys = new Set(merged.map((item) => creatorKeyOf(item.creatorKey)));

  workflowMap.forEach((workflow, key) => {
    if (mergedKeys.has(key)) return;

    merged.push(
      normalizeWorkflowRecord({
        ...CREATOR_WORKFLOW_DEFAULTS,
        ...workflow,
        creatorKey: key,
        username: stripAt(workflow.username || ""),
        displayName: norm(
          workflow.displayName || workflow.username || key || "Creator"
        ),
        creatorRef: workflow.creatorRef ?? null,
      })
    );
  });

  return merged.map((item) => ({
    ...CREATOR_WORKFLOW_DEFAULTS,
    ...item,
  }));
}